import React, { useEffect, useRef, useState } from 'react'

import { Box, Stack } from '@mui/material'

import { Header } from 'components/navigation/Header/Header'

/**
 * Width of `SectionNav`'s wide TOC rail. Mirrored by `TocSpacer` below, so the form column
 * centres on ITSELF rather than on the (rail + column) group. Must track
 * `packages/ui/components/navigation/SectionNav`.
 */
const TOC_RAIL_WIDTH = 200

/** Top padding of the app's scroll container (`Layout` main, p:6). See `::before` below. */
const SCROLLPORT_PAD = 24

/*
 * THE STUCK EDGE IS A HAIRLINE, AND ONLY A HAIRLINE.
 *
 * There was an `edge: 'border' | 'fade'` prop, and the card feeds (`/whats-new`, the two
 * request lists) passed `fade` on the argument that a rule over a column of bordered cards
 * reads as one more card edge. Removed rather than left as an option, because the fade could
 * not be seen and no amount of tuning could make it so.
 *
 * The measurement, taken on the running app: page background is rgb(9,9,11) and a card is
 * rgb(15,15,18) — 6-7 per channel, about 2.7%. The gradient painted PAGE BACKGROUND over the
 * card sliding beneath it, so what it dissolved was near-black into near-black. Height is not
 * the free variable it looks like: a taller gradient spreads the same 2.7% delta over more
 * pixels, which makes it fainter per pixel, not more visible.
 *
 * The hairline replacing it is `primary[100]` = rgb(39,39,42) against that same background:
 * 31/255, about 12% — roughly four times the contrast, on one crisp pixel rather than sixteen
 * soft ones.
 *
 * What DOES work elsewhere in the app (`MultiSelectFilter`, `Filters`, `Sort`) animates
 * `mask-image` on the CONTENT, so the text and borders themselves fade to transparent —
 * properties with real contrast against the background rather than against each other. Doing
 * that here needs the sticky header to sit outside the scroll container, which is a structural
 * change to every page using this component, not a prop.
 *
 * So: one edge treatment, the hairline. An option nobody can see is a trap for whoever reaches
 * for it next.
 */

/**
 * Nearest scrollable ancestor — the app scrolls inside <main>, not the window.
 *
 * Deliberately does NOT require `scrollHeight > clientHeight`. That check was here and it
 * silently broke any page whose content arrives AFTER mount: this runs in an effect on
 * first render, when a data-driven page is still showing its skeleton and nothing
 * overflows yet, so it returned null, no scroll listener was ever attached, and the stuck
 * state could never fire. The header stayed sticky — that part is pure CSS — while its
 * border/fade never appeared, which reads as a styling bug and is actually a missing
 * subscription. Caught on `/whats-new`, whose feed arrives from an API call.
 *
 * The size test was the wrong question anyway: `overflow: auto` marks where scrolling WILL
 * happen, whether or not the content overflows at this instant.
 */
const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el = node?.parentElement ?? null
  while (el) {
    const oy = getComputedStyle(el).overflowY
    if (oy === 'auto' || oy === 'scroll') return el
    el = el.parentElement
  }
  return null
}

interface StickyFormHeaderProps {
  title: string
  /**
   * Rendered inline after the title. Forwarded straight to `Header`, which has had the
   * slot all along — this component simply never passed it on, so a form header could
   * not annotate its own title while a page header could.
   */
  titleAdornment?: React.ReactNode
  /** Action buttons — one primary, rightmost (working-conventions §26/§33). */
  actions?: React.ReactNode
  /** Rendered under the title, inside the sticky block. The field-search box, in practice. */
  children?: React.ReactNode
  /** Record count beside the title, forwarded to `Header`. */
  total?: number | boolean
  /** Forwarded to `Header` — set when this sits under a page-level header (see `top`). */
  nested?: boolean
  /**
   * Where the block pins, in px. Defaults to 0 — the top of the scrollport.
   *
   * Non-zero when something else is ALREADY sticky above it: a settings section pins
   * below the shell's own "Settings" header, whose height is not a constant (it grows
   * by the back-link row when one renders). Pass the measured offset rather than
   * assuming; pinning at 0 would park this block underneath that header, where it is
   * invisible but still covering content.
   */
  top?: number
  /**
   * Draw the stuck-state hairline under the block. Default true.
   *
   * Set FALSE when `children` already ends in a rule of its own, or the two stack into
   * two hairlines a few pixels apart — measured on Studio, whose tab row draws its own
   * border at 145.5 while this one landed at 162.5: 17px apart, same weight, and
   * `rgb(39,39,40)` against `rgb(39,39,42)`, which no eye separates. It reads as a
   * rendering fault rather than as two deliberate edges.
   *
   * The tab row's border is the one to keep — it is present at rest too, and it belongs
   * to the tabs rather than to the scroll state. So the block yields instead.
   */
  stuckEdge?: boolean
  /**
   * Spacing units below the block's last row, in theme units (4px each). Default 4 = 16px.
   *
   * Set 0 when the block's own last row already draws the boundary — a tab list does,
   * because its border is present at rest and belongs to the tabs rather than to the
   * scroll state. In that case the padding is not breathing room, it is an opaque band
   * of page background between the tab border and the first card, and it reads as a bar
   * across the page. Studio is the case that named it.
   *
   * NOTE: this changes the block's HEIGHT, so any sibling pinned beneath it (Studio's
   * preview pane pins at `STUDIO_PANE_STICKY_TOP`) has to move by the same amount or it
   * will tuck under the block or float below it.
   */
  bottomGap?: number
}

/**
 * The title + actions block for a long create/edit form, sticky to the top of the scroll.
 *
 * Replaces the full-width action bar these forms pinned to the BOTTOM of the viewport. That
 * bar was permanent chrome — it rendered on a pristine create form with nothing entered, so
 * the strip and its border carried no state, unlike a Shopify-style save bar that EARNS the
 * weight by appearing only when there are unsaved changes. It also spanned the whole viewport
 * while the form column is much narrower, so its right edge aligned with nothing on the page.
 *
 * Shared rather than repeated: this is the third form to need it, and the three fiddly parts
 * below (the scrollport-padding mask, the scroll listener, the stuck-only border) are exactly
 * the kind of detail that drifts when it is copied (§36).
 */
export const StickyFormHeader = ({
  title,
  titleAdornment,
  actions,
  children,
  total,
  nested = false,
  top = 0,
  stuckEdge = true,
  bottomGap = 4,
}: StickyFormHeaderProps) => {
  const ref = useRef<HTMLDivElement>(null)
  // Has the page scrolled at all? Drives the bottom border, which should appear only once the
  // header is actually OVERLAYING content — at rest it covers nothing, and a permanent rule
  // under the title is chrome the resting state doesn't need.
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const sc = getScrollParent(ref.current)
    if (!sc) return
    const onScroll = () => setScrolled(sc.scrollTop > 0)
    sc.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => sc.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <Box
      ref={ref}
      sx={(theme) => ({
        position: 'sticky',
        top,
        zIndex: 2,
        bgcolor: 'background.default',
        // No top padding. Header positions its title row on the sidebar logo's centre line
        // measured from the scrollport's own 24px padding edge, so any padding added HERE
        // shifts the title off that line — the 8px this used to carry put every create/edit
        // form's title at 52 against a datum of 44. The `::before` band below is what gives
        // the stuck state its clearance; it does not need padding as well.
        pt: 0,
        // Breathing room under the title before the first card passes beneath.
        // See `bottomGap` — 0 when the block's last row draws its own boundary.
        pb: bottomGap,
        // A sticky element parks at the scroll container's PADDING edge, so the container's
        // 24px of top padding stayed transparent and cards were visible sliding through the
        // band above the title. This paints that band with the page background. A
        // pseudo-element rather than negative margins, which would move the title itself.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          top: -SCROLLPORT_PAD,
          height: SCROLLPORT_PAD,
          bgcolor: 'background.default',
        },
        // Transparent rather than absent at rest, so the border's 1px is always in the
        // box and turning it on cannot nudge the content below by a pixel.
        // `1px solid transparent` even when the edge is off, so the block's height never
        // changes with scroll state — a border appearing on scroll would shift every
        // pixel below it by one.
        borderBottom: `1px solid ${
          stuckEdge && scrolled ? theme.palette.primary[100] : 'transparent'
        }`,
        transition: 'border-color 0.15s ease',
      })}
    >
      <Header
        title={title}
        titleAdornment={titleAdornment}
        total={total}
        nested={nested}
        sx={{ pb: 0 }}
        rightComponents={
          actions ? (
            <Stack direction="row" spacing={4} alignItems="center">
              {actions}
            </Stack>
          ) : undefined
        }
      />
      {/* Inside the sticky block, not below it: the field-search is the FINDER for a form long
          enough to need a table of contents, so it is least useful at exactly the moment it
          would scroll away — once you are deep in the form.

          The gap RESTORES the `pb` this component just zeroed on `Header`, rather than being a
          number of its own. `Header` gives a page header `pb: 6` and a `nested` section header
          `pb: 3`, and that padding is the title's gutter to whatever follows it — so when the
          children ARE what follows, the same value is what keeps the title sitting the same
          distance from its controls as it does from its content. Hard-coding 6 put a nested
          header's controls 12px lower than the un-sticky page had them, which is a visible
          shift on a surface whose skeleton is measured to the pixel. */}
      {children ? <Box sx={{ mt: nested ? 3 : 6 }}>{children}</Box> : null}
    </Box>
  )
}

/**
 * Mirror of the TOC rail, placed AFTER the form column so the column is optically centred.
 *
 * Without it the flex row centres the (rail + column) group: the gaps either side are exactly
 * equal, but the left one terminates at transparent TOC text while the right terminates at the
 * card's filled edge — so the eye reads past the rail to the card and sees them as unequal
 * (measured 725 vs 501 on the product form). A spacer rather than absolutely positioning the
 * rail, so everything stays in flow and the sticky rail and the responsive column/row switch
 * keep working untouched.
 */
export const TocSpacer = () => (
  <Box aria-hidden sx={{ width: TOC_RAIL_WIDTH, flexShrink: 0, display: { xs: 'none', md: 'block' } }} />
)
